<script setup>
import { ref, computed, watch, getCurrentInstance } from 'vue';
import { useToast } from 'primevue/usetoast';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const toast = useToast();

const visible = ref(false);
const loading = ref(false);
const data = ref([]);
const includeDeleted = ref('all');

const deletedOptions = computed(() => [
    { label: $t('All'), value: 'all' },
    { label: $t('Active only'), value: 'active' },
]);

async function fetchData() {
    loading.value = true;
    data.value = [];
    try {
        const params = new URLSearchParams({ include_deleted: includeDeleted.value === 'all' ? '1' : '0' });
        const url = route('sellers.export-preview') + '?' + params.toString();
        const res = await fetch(url, {
            headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
        });
        const json = await res.json();
        let rows = json.data || [];
        data.value = rows;
    } catch (e) {
        toast.add({ severity: 'error', summary: $t('Error'), detail: $t('Failed to load preview'), life: 3000 });
        visible.value = false;
    } finally {
        loading.value = false;
    }
}

watch(includeDeleted, fetchData);

async function open() {
    includeDeleted.value = 'all';
    visible.value = true;
    await fetchData();
}

function download() {
    const includeDeletedParam = includeDeleted.value !== 'active' ? '1' : '0';
    window.location.href = route('sellers.export') + '?include_deleted=' + includeDeletedParam;
}

defineExpose({ open });
</script>

<template>
    <Dialog
        v-model:visible="visible"
        :header="$t('Export Preview')"
        :modal="true"
        :style="{ width: '92vw', maxWidth: '1100px' }"
        :breakpoints="{ '768px': '98vw' }"
    >
        <div v-if="loading" class="flex justify-center items-center py-12">
            <ProgressSpinner style="width: 48px; height: 48px" />
            <span class="ml-4 text-surface-500">{{ $t('Loading...') }}</span>
        </div>

        <template v-else>
            <div class="flex items-center justify-between mb-4 gap-4 flex-wrap">
                <span class="text-sm text-surface-500">
                    {{ data.length }} {{ $t('sellers') }} {{ $t('will be exported') }}
                </span>
                <div class="flex items-center gap-2">
                    <label class="text-sm font-medium text-surface-700">{{ $t('Deleted sellers') }}:</label>
                    <SelectButton
                        v-model="includeDeleted"
                        :options="deletedOptions"
                        optionLabel="label"
                        optionValue="value"
                    />
                </div>
            </div>

            <DataTable
                :value="data"
                :paginator="true"
                :rows="15"
                scrollable
                scrollHeight="420px"
                class="p-datatable-sm"
                :rowsPerPageOptions="[15, 30, 50]"
            >
                <Column field="name" :header="$t('Name')" sortable style="min-width: 160px" />
                <Column field="email" :header="$t('Email')" sortable style="min-width: 200px">
                    <template #body="{ data: row }">
                        <span class="text-sm">{{ row.email || '-' }}</span>
                    </template>
                </Column>
                <Column field="phone" :header="$t('Phone')" style="min-width: 130px">
                    <template #body="{ data: row }">
                        <span class="text-sm">{{ row.phone || '-' }}</span>
                    </template>
                </Column>
                <Column field="active_sites_count" :header="$t('Active sites')" sortable style="min-width: 110px">
                    <template #body="{ data: row }">
                        <Tag
                            :value="String(row.active_sites_count ?? 0)"
                            :severity="(row.active_sites_count ?? 0) > 0 ? 'success' : 'secondary'"
                        />
                    </template>
                </Column>
                <Column field="created_at" :header="$t('Created')" sortable style="min-width: 130px">
                    <template #body="{ data: row }">
                        <span class="text-sm">{{ row.created_at || '-' }}</span>
                    </template>
                </Column>
                <Column field="deleted_at" :header="$t('Deleted at')" style="min-width: 130px">
                    <template #body="{ data: row }">
                        <Tag v-if="row.deleted_at" :value="row.deleted_at" severity="danger" class="text-xs" />
                        <span v-else class="text-surface-300 text-sm">-</span>
                    </template>
                </Column>
            </DataTable>
        </template>

        <template #footer>
            <Button
                :label="$t('Cancel')"
                icon="pi pi-times"
                severity="secondary"
                text
                @click="visible = false"
            />
            <Button
                :label="$t('Download Excel')"
                icon="pi pi-file-excel"
                severity="success"
                :disabled="loading || data.length === 0"
                @click="download"
            />
        </template>
    </Dialog>
</template>
